<script setup lang="ts">
import { Quria, DestinyComponentType, DestinyCharacterResponse, BungieMembershipType, GroupsForMemberFilter, GroupType } from 'quria'
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import { type Account, type AccountResult, ErrorToast, TheFooter, useMediaQuery, formatUsername, usePlaywire, triggerRefresh } from '@wastedondestiny/destiny-library'
import SearchBar from '../components/SearchBar.vue'
import AccountCard from '../components/AccountCard.vue'
import { FinalShapeDungeonHashes, LightfallDungeonHashes, WitchQueenDungeonHashes } from '../enums/dungeons'
import '@splidejs/vue-splide/css'
import { releaseDetail } from '../releaseDetail'
import MainLogo from '../components/MainLogo.vue'
import { Expansions } from '../enums/expansions'

const { getRamp } = usePlaywire()
const route = useRoute()
const router = useRouter()
const isNotMobile = useMediaQuery('(min-width: 640px)')
const accounts = ref([] as Account[])
const isOpen = ref({
  raid: true,
  dungeon: false,
  pinnacle: false,
  fireteam: false,
  solo: false,
  arena: false,
  core: false,
  expansion: false,
})
const rotations = ref<Record<string, string>>({})
const loading = ref(false)
const animating = ref(false)
const errors = ref([] as Error[])
const quria = new Quria({
  API_KEY: import.meta.env.VITE_BUNGIE_API_KEY
})
const logo = ref<HTMLElement>()

function removeError(index: number) {
  const spliced = errors.value
  spliced.splice(index, 1)
  errors.value = spliced
}

function toggle(tag: keyof typeof isOpen.value) {
  isOpen.value[tag] = !isOpen.value[tag]
}

function getDungeonHashes(character: DestinyCharacterResponse) {
  let content: number[] = []
  const dungeonHashes = [...Object.values(WitchQueenDungeonHashes), ...Object.values(LightfallDungeonHashes), ...Object.values(FinalShapeDungeonHashes)]
  const dungeonActivities = character?.activities?.data?.availableActivities?.filter((x: any) => dungeonHashes.includes(x.activityHash) && x.canJoin)

  if (dungeonActivities?.length) content = [...content, ...dungeonActivities.map((x: any) => x.activityHash as number)]

  return content
}

async function addSearchAccount(accountResult: AccountResult) {
  if (accounts.value.find(x => x.membershipId === accountResult.membershipId)) return

  setLoading(true)

  try {
    await load(accountResult.membershipId)
  } catch (error: Error|unknown) {
    console.error(error)
    if (error instanceof Error) errors.value = [...errors.value, error]
  }

  let accountIds = accounts.value.map(x => x.membershipId)
  accountIds = [...new Set(accountIds)]
  router.replace({ name: 'HomePage', params: { memberships: accountIds } })
  setLoading(false)
}

async function removeAccount(account: Account) {
  if (!accounts.value.find(x => x.membershipId === account.membershipId)) return

  accounts.value = accounts.value.filter(x => x.membershipId !== account.membershipId)
  let accountIds = accounts.value.map(x => x.membershipId)
  accountIds = [...new Set(accountIds)]
  router.replace({ name: 'HomePage', params: { memberships: accountIds } })
}

async function load(membershipId: string) {
  if (accounts.value.find(x => x.membershipId === membershipId)) return

  const linkedProfiles = await quria.destiny2.GetLinkedProfiles(membershipId, BungieMembershipType.All, { getAllMemberships: true })
  if (!linkedProfiles.Response) throw new Error('Unable to find this account.')
  const userInfo = linkedProfiles.Response.profiles.find(x => x.membershipId === membershipId)
  if (!userInfo) throw new Error('Unable to find this account.')
  const profile = await quria.destiny2.GetProfile(userInfo.membershipId, userInfo.membershipType, {
    components: [ DestinyComponentType.Profiles, DestinyComponentType.Transitory ]
  })
  if (!profile.Response?.profile?.data) throw new Error('Unable to find this account.')
  const clan = await quria.groupv2.GetGroupsForMember(GroupsForMemberFilter.All, GroupType.Clan, userInfo.membershipId, userInfo.membershipType)

  const account: Account = {
    membershipId: userInfo.membershipId,
    membershipType: userInfo.membershipType,
    crossSaveOverride: userInfo.crossSaveOverride,
    displayName: formatUsername(userInfo.bungieGlobalDisplayName, userInfo.bungieGlobalDisplayNameCode || 0, userInfo.displayName),
    clan: clan.ErrorCode === 1 && clan.Response.results?.[0]?.group ? {
      name: clan.Response.results[0].group.name,
      id: clan.Response.results[0].group.groupId
    } : undefined,
    characters: profile.Response.profile.data.characterIds.map(x => ({ characterId: x })),
    versionsOwned: profile.Response.profile.data.versionsOwned,
    seasonsOwned: [...profile.Response.profile.data.seasonHashes, ...profile.Response.profile.data.seasonPassHashes],
    dungeonsOwned: [],
    fireteam: profile.Response.profileTransitoryData?.data?.partyMembers?.map(x => x.membershipId) ?? [],
    private: false
  }

  const debugInfo: Record<string, any> = {}

  debugInfo['Base game'] = ((account.versionsOwned ?? 0) & Expansions.Destiny2) === Expansions.Destiny2
  debugInfo['Curse of Osiris'] = ((account.versionsOwned ?? 0) & Expansions.CurseOfOsiris) === Expansions.CurseOfOsiris
  debugInfo['Warmind'] = ((account.versionsOwned ?? 0) & Expansions.Warmind) === Expansions.Warmind
  debugInfo['Forsaken'] = ((account.versionsOwned ?? 0) & Expansions.Forsaken) === Expansions.Forsaken
  debugInfo['Annual Pass'] = ((account.versionsOwned ?? 0) & Expansions.AnnualPass) === Expansions.AnnualPass
  debugInfo['Shadowkeep'] = ((account.versionsOwned ?? 0) & Expansions.Shadowkeep) === Expansions.Shadowkeep
  debugInfo['Beyond Light'] = ((account.versionsOwned ?? 0) & Expansions.BeyondLight) === Expansions.BeyondLight
  debugInfo['30th Anniversary'] = ((account.versionsOwned ?? 0) & Expansions.Anniversary30th) === Expansions.Anniversary30th
  debugInfo['The Witch Queen'] = ((account.versionsOwned ?? 0) & Expansions.TheWitchQueen) === Expansions.TheWitchQueen
  debugInfo['Lightfall'] = ((account.versionsOwned ?? 0) & Expansions.Lightfall) === Expansions.Lightfall
  debugInfo['Final Shape'] = ((account.versionsOwned ?? 0) & Expansions.FinalShape) === Expansions.FinalShape
  debugInfo['Edge of Fate'] = ((account.versionsOwned ?? 0) & Expansions.EdgeOfFate) === Expansions.EdgeOfFate
  debugInfo['Renegades'] = ((account.versionsOwned ?? 0) & Expansions.Renegades) === Expansions.Renegades
  debugInfo['All Expansions'] = account.versionsOwned

  const knownSeasonIds = Array.from(releaseDetail.values())
    .filter(x => x.name.startsWith('Season') || x.name.startsWith('Episode'))
    .map(x => x.id)
  const allSeasonIds = Array.from(new Set(knownSeasonIds.concat(account.seasonsOwned ?? [])))

  for (const seasonId of allSeasonIds) {
    debugInfo[releaseDetail.get(seasonId)?.name ?? `Unknown season: ${seasonId}`] = (account.seasonsOwned ?? []).includes(seasonId)
  }

  for (const character of account.characters) {
    const characterProfile = await quria.destiny2.GetCharacter(character.characterId, account.membershipId, account.membershipType!, {
      components: [ DestinyComponentType.CharacterActivities ]
    })
    account.dungeonsOwned = [...account.dungeonsOwned!, ...getDungeonHashes(characterProfile.Response)]

    if (!characterProfile.Response?.activities?.data) {
      account.private = true
      
      // Short-circuit for privacy
      throw new Error('This account is private. You can change your privacy settings on <a target="_blank" href="https://www.bungie.net/7/en/User/Account/Privacy">Bungie.net</a>.', {
        cause: 'privacy'
      })
    }
  }

  // console.log(`Account ${userInfo.membershipId}`, account.displayName)
  // console.table(debugInfo)
  getRamp().then(() => triggerRefresh())
  accounts.value.push(account)
}

async function loadFromUrl() {
  if (!route.params.memberships?.length) return

  scrollTo({
    top: 0,
    behavior: 'smooth'
  })
  setLoading(true)
  const accountPromises: Promise<void>[] = []

  for (const membershipId of (route.params.memberships as string[])) {
    accountPromises.push((async () => {
      try {
        await load(membershipId)
      } catch (error: Error|unknown) {
        console.error(error)
        if (error instanceof Error) errors.value = [...errors.value, error]
      }
    })())
  }

  await Promise.allSettled(accountPromises)
  let accountIds = accounts.value.map(x => x.membershipId)
  accountIds = [...new Set(accountIds)]
  router.replace({ name: 'HomePage', params: { memberships: accountIds } })
  setLoading(false)
}

watch(() => route.params, () => {
  if (route.params.memberships?.length && (route.params.memberships as string[]).some(x => !accounts.value.some(y => y.membershipId === x))) {
    loadFromUrl()
  }
})

const creatorLink = computed(() => ({
  name: 'HomePage',
  params: {
    memberships: (route.params.memberships as string[] || []).filter(x => x !== '4611686018467210709').concat([ '4611686018467210709' ])
  }
}))

onMounted(() => {
  preventMutations()
  loadFromUrl()
})

function preventMutations() {
  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        if (mutation.target instanceof HTMLElement && mutation.target.getAttribute('style')?.includes('important')) {
          mutation.target.removeAttribute('style')
        }
      }
    }
  })

  observer.observe(logo.value!.getRootNode(), {
    attributes: true,
    subtree: true
  })
}

function setLoading(value: boolean) {
  loading.value = value
  setTimeout(() => {
    animating.value = value
  }, 500)
}

async function loadFireteam(members: string[]) {
  setLoading(true)
  const accountPromises: Promise<void>[] = []
  
  for (const membershipId of members) {
    accountPromises.push((async () => {
      try {
        await load(membershipId)
      } catch (error: Error|unknown) {
        console.error(error)
        if (error instanceof Error) errors.value = [...errors.value, error]
      }
    })())
  }

  await Promise.allSettled(accountPromises)
  let accountIds = accounts.value.map(x => x.membershipId)
  accountIds = [...new Set(accountIds)]
  router.replace({ name: 'HomePage', params: { memberships: accountIds } })
  setLoading(false)
}

defineExpose({ toggle })
</script>

<template>
  <div class="flex flex-col items-center justify-center h-fit screen">
    <div v-if="!accounts.length" class="flex-grow" />
    <div class="text-center px-1 mt-4 mb-10 text-zinc-200" ref="logo">
      <MainLogo class="max-w-full w-[30rem] sm:w-[40rem] lg:w-[50rem]" />
      <h1 class="text-4xl font-bold shadow-black/50 text-shadow-sm hidden">crimson.report</h1>
      <h2 class="text-lg md:text-xl font-thin shadow-black/50 text-shadow-sm hidden">Check what content you can play with your friends on Destiny 2!</h2>
    </div>
    <div class="mb-10 w-full transition-all duration-500 shadow-lg shadow-black/50 rounded-3xl bg-white" :class="{ 'expanded-search': !loading, 'collapsed-search': loading }">
      <div class="relative">
        <div class="py-2 w-[6.5rem] h-12" />
        <div v-if="loading || animating" class="absolute top-0 right-0 w-24 h-10 p-1 m-1 rounded-3xl bg-rose-500 text-zinc-100 text-3xl leading-7 font-serif font-bold flex gap-2 justify-center">
          <span class="transition-opacity bullet-1">&bullet;</span>
          <span class="transition-opacity bullet-2">&bullet;</span>
          <span class="transition-opacity bullet-3">&bullet;</span>
        </div>
      </div>
      <SearchBar class="-mt-12" v-if="!loading && !animating" @search="addSearchAccount" />
    </div>
    <!-- <div class="text-zinc-300 flex flex-col items-center mb-8 -mt-4">
      <small>NEW DROP (POD) INCOMING!</small>
      <a href="https://guesstiny.com/" target="_blank" class="block w-full max-w-[320px] rounded-lg overflow-hidden shadow-lg relative after:absolute after:inset-0 hover:after:bg-white/25">
        <img src="../assets/guesstiny.png" alt="GUESSTINY.COM - From Nessus to the Dreaming City, Challenge your knowledge of Destiny" />
      </a>
    </div> -->
    <div v-if="accounts.length" class="flex w-full mb-10 xl:px-48 splide-with-fade">
      <Splide :options="{ pagination: false, autoWidth: true, focus: 'center', padding: 8 }" class="w-full">
        <SplideSlide v-for="account in accounts.slice(0, Math.ceil(accounts.length / 2))" :key="account.membershipId" class="px-2">
          <AccountCard :account="account" :accounts="accounts" :rotations="rotations" :loading="loading" :is-open="isOpen" @toggle="toggle" @remove="removeAccount" @loadFireteam="loadFireteam" />
        </SplideSlide>
        <SplideSlide v-if="isNotMobile && accounts.length % 2 === 0" />
        <SplideSlide v-for="account in accounts.slice(Math.ceil(accounts.length / 2))" :key="account.membershipId" class="px-2">
          <AccountCard :account="account" :accounts="accounts" :rotations="rotations" :loading="loading" :is-open="isOpen" @toggle="toggle" @remove="removeAccount" @loadFireteam="loadFireteam" />
        </SplideSlide>
      </Splide>
    </div>
    <div v-else class="flex-grow" />
    <TheFooter name="crimson.report" use-playwire>
      <RouterLink class="!decoration-1 !decoration-dotted" :to="creatorLink">&copy; Karmageddon Games {{ (new Date()).getFullYear() }}</RouterLink>
      <template #privacy>
        <RouterLink class="!decoration-1 !decoration-dotted" to="/privacy">Privacy</RouterLink>
      </template>
      <template #ad>
        <!-- <ins class="adsbygoogle w-[320px] h-[100px] sm:w-[468px] sm:h-[60px] lg:w-[728px] lg:h-[90px] block z-10 mx-auto"
          data-ad-client="ca-pub-6332373031553935"
          data-ad-slot="9971297121"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins> -->
        <div id="footer" class="w-full h-[280px] mx-auto flex justify-center"></div>
      </template>
    </TheFooter>
    <div class="fixed bottom-0 flex flex-col-reverse gap-2 mb-4" style="z-index: 9999;">
      <ErrorToast v-for="(error, i) in errors" :key="i" @close="() => removeError(i)">
        <span v-if="error.cause === 'privacy'" v-html="error.message" />
        <span v-else>{{ error?.message || error }}</span>
      </ErrorToast>
    </div>
  </div>
</template>

<style lang="postcss">
html {
  scroll-behavior: smooth;
}

.screen {
  min-height: calc(100dvh - 8rem);
}

.expanded-search {
  max-width: 18rem;
}

.collapsed-search {
  max-width: 6.5rem;
}

@media (min-width: 768px) {
  .expanded-search {
    max-width: 24rem;
  }
}

.bullet-1 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.1s;
}

.bullet-2 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.2s;
}

.bullet-3 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.3s;
}

@keyframes dot {
  0% { opacity: 0; }
 20% { opacity: 1; }
100% { opacity: 0; }
}

.reduced-motion .bullet-1, .reduced-motion .bullet-2, .reduced-motion .bullet-3 {
  animation-play-state: paused;
  opacity: 1;
}

@media (prefers-reduced-motion) {
  .bullet-1, .bullet-2, .bullet-3 {
    animation-play-state: paused;
    opacity: 1;
  }
}

/* Splide fade gradient effect */
.splide-with-fade {
  position: relative;
}

.splide-with-fade .splide__track {
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
}

/* Fallback for older browsers that don't support mask-image */
@supports not (mask-image: linear-gradient(to right, black, black)) {
  .splide-with-fade {
    position: relative;
  }
  
  .splide-with-fade::before,
  .splide-with-fade::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 16px;
    z-index: 10;
    pointer-events: none;
  }
  
  .splide-with-fade::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-color, #1f2937), transparent);
  }
  
  .splide-with-fade::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-color, #1f2937), transparent);
  }
}
</style>
